import { Link, usePage } from '@inertiajs/react';
import { ChevronDown, ChevronRight, LogOut, User } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { csrfToken } from '@/lib/csrf';
import { cn } from '@/lib/utils';
import type { Hacker } from '@/types';
import {
  buildNavigation,
  isItemActive,
  type NavGroup,
  type NavItem,
} from './sidebar-nav';

// The hacker portal sidebar — one component for the four
// app/(hack)/**/hacker/(protected)/_components/hacker-sidebar.tsx twins (PORTING.md G5).
// Which entries it shows comes from sidebar-nav.ts, keyed by the edition the server
// resolved onto the `hackerPortal` shared prop.

interface HackerSidebarProps {
  edition: string | null;
  hacker: Hacker;
  discordGuildId: string | null;
  discordFaqChannelId: string | null;
  showMusic: boolean;
}

function itemClass(active: boolean) {
  return cn(
    'flex items-center gap-3 border-2 px-3 py-2 font-mono text-sm transition-all',
    active
      ? 'border-primary bg-primary text-background'
      : 'border-transparent text-primary hover:border-primary hover:bg-primary/10',
  );
}

function NavItemBody({ item }: { item: NavItem }) {
  if (item.logo) {
    return (
      <span
        aria-hidden="true"
        className={cn('block shrink-0', item.logo.heightClass)}
        style={{
          aspectRatio: item.logo.aspect,
          backgroundColor: 'currentColor',
          maskImage: `url(${item.logo.src})`,
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'left center',
          WebkitMaskImage: `url(${item.logo.src})`,
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'left center',
        }}
      />
    );
  }
  return (
    <>
      <item.icon className="h-5 w-5" />
      {item.name}
    </>
  );
}

function NavItemLink({
  item,
  pathname,
}: {
  item: NavItem;
  pathname: string;
}) {
  const isActive = !item.external && isItemActive(pathname, item.href);
  const ariaLabel = item.logo ? item.name : undefined;

  if (item.comingSoon) {
    return (
      <span
        aria-disabled="true"
        aria-label={ariaLabel && `${ariaLabel} (próximamente)`}
        title="Próximamente"
        className="flex cursor-not-allowed items-center gap-3 border-2 border-transparent px-3 py-2 font-mono text-primary text-sm opacity-40"
      >
        <NavItemBody item={item} />
        <span className="ml-auto text-[10px] uppercase tracking-wide">
          pronto
        </span>
      </span>
    );
  }

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        className={itemClass(false)}
      >
        <NavItemBody item={item} />
      </a>
    );
  }

  return (
    <Link
      href={item.href}
      aria-label={ariaLabel}
      className={itemClass(isActive)}
    >
      <NavItemBody item={item} />
    </Link>
  );
}

function NavGroupBlock({
  group,
  pathname,
}: {
  group: NavGroup;
  pathname: string;
}) {
  const hasActiveChild = group.children.some(
    (child) => !child.external && isItemActive(pathname, child.href),
  );
  const [open, setOpen] = useState(hasActiveChild);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex w-full items-center gap-3 border-2 px-3 py-2 font-mono text-sm transition-all',
          hasActiveChild
            ? 'border-primary text-primary'
            : 'border-transparent text-primary hover:border-primary hover:bg-primary/10',
        )}
      >
        <group.icon className="h-5 w-5" />
        <span className="flex-1 text-left">{group.name}</span>
        <Chevron className="h-4 w-4" />
      </button>
      {open && (
        <div className="ml-4 space-y-1 border-primary/30 border-l-2 pl-2">
          {group.children.map((child) => (
            <NavItemLink key={child.name} item={child} pathname={pathname} />
          ))}
        </div>
      )}
    </div>
  );
}

export function HackerSidebar({
  edition,
  hacker,
  discordGuildId,
  discordFaqChannelId,
  showMusic,
}: HackerSidebarProps) {
  const page = usePage();
  // `usePathname()` over there; Inertia's page url carries the query string.
  const pathname = page.url.split('?')[0];
  const navigation = buildNavigation(edition, {
    discordGuildId,
    discordFaqChannelId,
    showMusic,
  });
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const githubUsername = hacker.github
    ? hacker.github.split('/').pop() || 'Usuario'
    : page.props.auth.user?.name || 'Usuario';

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await fetch('/api/auth/sign-out', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
      });
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="flex h-full flex-col border-primary border-r-2 bg-background/80 backdrop-blur-sm">
      <div className="flex h-16 items-center border-primary border-b-2 px-4">
        <div
          className="aspect-[576/112] h-6 w-auto"
          style={{
            backgroundColor: 'hsl(var(--primary))',
            maskImage: 'url(/assets/logos/platanus.svg)',
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskImage: 'url(/assets/logos/platanus.svg)',
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
          }}
        />
      </div>

      <nav className="flex-1 space-y-1 p-4">
        {navigation.map((entry) =>
          entry.kind === 'group' ? (
            <NavGroupBlock key={entry.name} group={entry} pathname={pathname} />
          ) : (
            <NavItemLink key={entry.name} item={entry} pathname={pathname} />
          ),
        )}
      </nav>

      {/* User info and logout */}
      <div className="border-primary border-t-2 p-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 border-2 border-primary/20 bg-primary/5 px-3 py-2">
            <User className="h-4 w-4 text-primary" />
            <span className="truncate font-mono text-primary text-sm">
              @{githubUsername}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full border-2 border-primary font-mono text-primary hover:bg-primary hover:text-background"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {isLoggingOut ? 'Cerrando...' : 'Cerrar sesión'}
          </Button>
        </div>
      </div>
    </div>
  );
}
